"use client"

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { ref, onValue, get, update } from "../lib/firebase-database";
import { Search, X, AlertTriangle, ArrowLeft, Building, User, FileText, Calendar as CalendarIcon } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Separator } from "./ui/separator";
import { Calendar } from "../components/ui/calendar";

export function AdminBookingPage() {
  const [villas, setVillas] = useState([]);
  const [filteredVillas, setFilteredVillas] = useState([]);
  const [selectedVilla, setSelectedVilla] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [villaSearchQuery, setVillaSearchQuery] = useState("");
  const [bookingSearchQuery, setBookingSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [showCalendarDialog, setShowCalendarDialog] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [showMonthlyBill, setShowMonthlyBill] = useState(false);
  const [monthlyBillData, setMonthlyBillData] = useState([]);
  const [totalBillAmount, setTotalBillAmount] = useState(0);

  const user = auth.currentUser;
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  // Check if user is admin
  useEffect(() => {
    if (!user || !isAdmin) {
      navigate("/");
      return;
    }
  }, [user, isAdmin, navigate]);

  // Fetch all villas
  useEffect(() => {
    if (!user) return;

    const villasRef = ref(db, "villas");
    const unsubscribe = onValue(villasRef, async (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setVillas([]);
        setFilteredVillas([]);
        setLoading(false);
        return;
      }

      const villasArray = await Promise.all(
        Object.entries(data).map(async ([id, villa]) => {
          const ownerName = villa.owner?.name || "Unknown";
          const ownerEmail = villa.ownerEmail || "Unknown";
          
          return {
            id,
            ...villa,
            ownerName,
            ownerEmail
          };
        })
      );

      setVillas(villasArray);
      setFilteredVillas(villasArray);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Filter villas based on search query
  useEffect(() => {
    if (!villas.length) return;

    if (!villaSearchQuery.trim()) {
      setFilteredVillas(villas);
      return;
    }

    const query = villaSearchQuery.toLowerCase();
    const filtered = villas.filter(
      (villa) =>
        villa.name?.toLowerCase().includes(query) ||
        villa.ownerName?.toLowerCase().includes(query) ||
        villa.ownerEmail?.toLowerCase().includes(query),
    );

    setFilteredVillas(filtered);
  }, [villaSearchQuery, villas]);

  // Fetch bookings for selected villa
  useEffect(() => {
    if (!selectedVilla) {
      setBookings([]);
      setFilteredBookings([]);
      return;
    }

    setLoading(true);
  
    const allBookingsRef = ref(db, "bookings");
    const unsubscribe = onValue(allBookingsRef, async (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setBookings([]);
        setFilteredBookings([]);
        setLoading(false);
        return;
      }

      const allBookings = [];
      const processedBookingIds = new Set();
      const bookedDatesList = [];
      
      for (const [userId, userBookings] of Object.entries(data)) {
        if (!userBookings) continue;
        
        for (const [bookingId, booking] of Object.entries(userBookings)) {
          if (booking.villaId === selectedVilla.id && !processedBookingIds.has(bookingId)) {
            processedBookingIds.add(bookingId);
            
            let userName = booking.userName || "Guest";
            let userEmail = booking.userEmail || "Unknown";
            let userPhone = booking.userPhone || booking.phoneNumber || "Not provided";
            
            if (userId) {
              const userRef = ref(db, `users/${userId}`);
              const userSnapshot = await get(userRef);
              const userData = userSnapshot.val();
              if (userData) {
                userName = userData.displayName || userData.name || userName;
                userEmail = userData.email || userEmail;
                userPhone = userData.phoneNumber || userPhone;
              }
            }
            
            allBookings.push({
              id: bookingId,
              userId,
              userName,
              userEmail,
              userPhone,
              ...booking,
              villaData: {
                name: selectedVilla.name || "Villa Name Not Available",
                thumbnail: selectedVilla.thumbnail || "/placeholder.svg",
                address: selectedVilla.address || "Address not available",
              }
            });
            
            // Validate dates before processing
            if (
              booking.checkIn &&
              booking.checkOut &&
              !isNaN(new Date(booking.checkIn)) &&
              !isNaN(new Date(booking.checkOut))
            ) {
              const checkInDate = new Date(booking.checkIn);
              const checkOutDate = new Date(booking.checkOut);
              
              const currentDate = new Date(checkInDate);
              while (currentDate <= checkOutDate) {
                bookedDatesList.push(new Date(currentDate));
                currentDate.setDate(currentDate.getDate() + 1);
              }
            }
          }
        }
      }
      
      setBookings(allBookings);
      setFilteredBookings(allBookings);
      setBookedDates(bookedDatesList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedVilla]);

  // Filter bookings based on search query and category
  useEffect(() => {
    if (!bookings.length) return;

    let filtered = [...bookings];
  
    if (bookingSearchQuery.trim()) {
      const query = bookingSearchQuery.toLowerCase();
      filtered = filtered.filter(
        booking => 
          booking.bookingId?.toLowerCase().includes(query) ||
          booking.userName?.toLowerCase().includes(query) ||
          booking.userEmail?.toLowerCase().includes(query) ||
          booking.userPhone?.toLowerCase().includes(query)
      );
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (activeCategory === "upcoming") {
      filtered = filtered.filter(b => new Date(b.checkIn) > tomorrow);
      filtered.sort((a, b) => {
        if (a.status === "cancelled" && b.status !== "cancelled") return 1;
        if (a.status !== "cancelled" && b.status === "cancelled") return -1;
        return 0;
      });
    } else if (activeCategory === "today") {
      filtered = filtered.filter(b => {
        const checkIn = new Date(b.checkIn);
        return checkIn.toDateString() === today.toDateString();
      });
      filtered.sort((a, b) => {
        if (a.status === "cancelled" && b.status !== "cancelled") return 1;
        if (a.status !== "cancelled" && b.status === "cancelled") return -1;
        return 0;
      });
    } else if (activeCategory === "past") {
      filtered = filtered.filter(b => new Date(b.checkOut) < today);
      filtered.sort((a, b) => {
        if (a.status === "cancelled" && b.status !== "cancelled") return 1;
        if (a.status !== "cancelled" && b.status === "cancelled") return -1;
        return 0;
      });
    } else if (activeCategory === "cancelled") {
      filtered = filtered.filter(b => b.status === "cancelled");
    } else {
      filtered.sort((a, b) => {
        if (a.status === "cancelled" && b.status !== "cancelled") return 1;
        if (a.status !== "cancelled" && b.status === "cancelled") return -1;
        return 0;
      });
    }
    
    setFilteredBookings(filtered);
  }, [bookingSearchQuery, bookings, activeCategory]);

  // Calculate monthly bill data for the current month
  useEffect(() => {
    if (!bookings.length) {
      setMonthlyBillData([]);
      setTotalBillAmount(0);
      return;
    }

    const filteredBookings = bookings.filter(booking => {
      if (!booking.checkIn) return false;
      const checkInDate = new Date(booking.checkIn);
      return checkInDate.getMonth() === currentMonth && 
             checkInDate.getFullYear() === currentYear &&
             booking.status !== "cancelled";
    });

    const billData = filteredBookings.map(booking => {
      const guestCount = booking.guests || 0;
      const amount = guestCount * 100;
      return {
        ...booking,
        billAmount: amount
      };
    });

    const total = billData.reduce((sum, booking) => sum + booking.billAmount, 0);

    setMonthlyBillData(billData);
    setTotalBillAmount(total);
  }, [bookings, currentMonth, currentYear]);

  // Format date helper function
  const formatDate = (timestamp, timing) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const formattedDate = `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${String(date.getFullYear()).slice(-2)}`;
    return timing ? `${formattedDate} (${timing})` : formattedDate;
  };

  // Handle booking cancellation
  const handleCancelBooking = (booking) => {
    setBookingToCancel(booking);
    setShowCancelDialog(true);
  };

  // Confirm booking cancellation
  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;

    try {
      if (selectedVilla.ownerId) {
        await update(ref(db, `bookings/${selectedVilla.ownerId}/${bookingToCancel.id}`), {
          status: "cancelled",
          cancelledAt: Date.now(),
          cancelledBy: user.uid,
          cancelledByAdmin: true,
        });
      }

      if (bookingToCancel.userId) {
        await update(ref(db, `bookings/${bookingToCancel.userId}/${bookingToCancel.id}`), {
          status: "cancelled",
          cancelledAt: Date.now(),
          cancelledBy: user.uid,
          cancelledByAdmin: true,
        });
      }

      await update(ref(db, `allBookings/${bookingToCancel.id}`), {
        status: "cancelled",
        cancelledAt: Date.now(),
        cancelledBy: user.uid,
        cancelledByAdmin: true,
      });

      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === bookingToCancel.id
            ? {
                ...booking,
                status: "cancelled",
                cancelledAt: Date.now(),
                cancelledBy: user.uid,
                cancelledByAdmin: true,
              }
            : booking,
        ),
      );

      setShowCancelDialog(false);
      setBookingToCancel(null);
    } catch (error) {
      console.error("Error cancelling booking:", error);
      alert("Failed to cancel booking. Please try again.");
    }
  };

  // Get count for each category
  const getCategoryCount = (category) => {
    if (!bookings.length) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    switch (category) {
      case "upcoming":
        return bookings.filter((b) => new Date(b.checkIn) > tomorrow).length;
      case "today":
        return bookings.filter((b) => {
          const checkIn = new Date(b.checkIn);
          return checkIn.toDateString() === today.toDateString();
        }).length;
      case "past":
        return bookings.filter((b) => new Date(b.checkOut) < today).length;
      case "cancelled":
        return bookings.filter((b) => b.status === "cancelled").length;
      default:
        return bookings.length;
    }
  };

  // Handle villa selection
  const handleVillaSelect = (villa) => {
    setSelectedVilla(villa);
    setActiveCategory("all");
    setBookingSearchQuery("");
  };

  // Handle back button
  const handleBack = () => {
    if (showMonthlyBill) {
      setShowMonthlyBill(false);
    } else if (selectedVilla) {
      setSelectedVilla(null);
    } else {
      navigate(-1);
    }
  };

  // Custom day renderer for the calendar
  const renderDay = (day) => {
    const date = day?.date; // Optional chaining to handle undefined
    if (!date || isNaN(date.getTime())) {
      console.error("Invalid date in renderDay:", day);
      return <div>-</div>; // Fallback to avoid crash
    }

    const isBooked = bookedDates.some(bookedDate => 
      bookedDate.getDate() === date.getDate() && 
      bookedDate.getMonth() === date.getMonth() && 
      bookedDate.getFullYear() === date.getFullYear()
    );
    
    return (
      <div className={`w-full h-full flex items-center justify-center rounded-full ${
        isBooked ? 'bg-red-100 text-red-900' : 'bg-green-100 text-green-900'
      }`}>
        {date.getDate()}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="sticky top-0 bg-white z-50 shadow-sm">
        <div className="flex items-center px-4 py-3">
          <button onClick={handleBack} className="flex items-center text-gray-800">
            <ArrowLeft className="h-6 w-6 mr-2" />
          </button>
          <h1 className="text-xl font-semibold ml-2">
            {showMonthlyBill 
              ? `Monthly Bill: ${months[currentMonth]} ${currentYear}` 
              : selectedVilla 
                ? `Bookings for ${selectedVilla.name}` 
                : "All Villas"}
          </h1>
        </div>
      </div>

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {!selectedVilla ? (
          // Villa listing view
          <>
            {/* Search Bar for Villas */}
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Search villas by name or owner..."
                  value={villaSearchQuery}
                  onChange={(e) => setVillaSearchQuery(e.target.value)}
                  className="pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full"
                />
                {villaSearchQuery && (
                  <button
                    onClick={() => setVillaSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <X className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            {/* Villa List */}
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : filteredVillas.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredVillas.map((villa) => (
                  <Card
                    key={villa.id}
                    className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleVillaSelect(villa)}
                  >
                    <div className="relative h-48">
                      <img
                        src={villa.thumbnail || "/placeholder.svg"}
                        alt={villa.name}
                        className="w-full h-full object-cover rounded-t-lg"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
                        <div className="p-4 w-full">
                          <h3 className="text-white text-xl font-bold">{villa.name}</h3>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Building className="h-4 w-4 text-gray-500" />
                        <p className="text-sm text-gray-500"/>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <p className="text-sm text-gray-600">Owner: {villa.ownerName}</p>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button size="sm" variant="outline">
                          View Bookings
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  {villaSearchQuery ? "No villas found matching your search." : "No villas available."}
                </p>
              </div>
            )}
          </>
        ) : showMonthlyBill ? (
          // Monthly Bill View
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="mb-6">
              <div className="bg-gray-100 p-4 rounded-lg">
                <h3 className="text-lg font-semibold">Total Bill Amount</h3>
                <p className="text-2xl font-bold">₹{totalBillAmount}</p>
                <p className="text-xs text-gray-500">Based on ₹100 per guest</p>
              </div>
            </div>

            {monthlyBillData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Booking ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Guest
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Check-in
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Guest Count
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount (₹100/guest)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {monthlyBillData.map((booking) => (
                      <tr key={booking.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{booking.bookingId?.slice(-6) || "000000"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {booking.userName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(booking.checkIn)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {booking.guests}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₹{booking.billAmount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  No bookings found for {months[currentMonth]} {currentYear}
                </p>
              </div>
            )}
          </div>
        ) : (
          // Bookings view for selected villa
          <>
            {/* Villa Info Card */}
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{selectedVilla.name}</CardTitle>
                    <CardDescription>{selectedVilla.address}</CardDescription>
                  </div>
                  <img
                    src={selectedVilla.thumbnail || "/placeholder.svg"}
                    alt={selectedVilla.name}
                    className="w-16 h-16 object-cover rounded-md"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
                  <div>
                    <p className="text-sm text-gray-500">Owner</p>
                    <p className="font-medium">{selectedVilla.ownerName}</p>
                    <p className="text-sm text-gray-500">{selectedVilla.ownerEmail}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Contact</p>
                    <p className="font-medium">{selectedVilla.whatsappNumber || "N/A"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 mb-6">
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => setShowCalendarDialog(true)}
              >
                <CalendarIcon className="h-4 w-4" />
                View Availability Calendar
              </Button>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => setShowMonthlyBill(true)}
              >
                <FileText className="h-4 w-4" />
                Monthly Bill
              </Button>
            </div>

            {/* Search Bar for Bookings */}
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Search by booking ID or guest name..."
                  value={bookingSearchQuery}
                  onChange={(e) => setBookingSearchQuery(e.target.value)}
                  className="pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full"
                />
                {bookingSearchQuery && (
                  <button
                    onClick={() => setBookingSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <X className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            {/* Booking Categories */}
            <Tabs defaultValue="all" value={activeCategory} onValueChange={setActiveCategory} className="mb-6">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="all">
                  All ({bookings.length})
                </TabsTrigger>
                <TabsTrigger value="upcoming">
                  Upcoming ({getCategoryCount("upcoming")})
                </TabsTrigger>
                <TabsTrigger value="today">
                  Today ({getCategoryCount("today")})
                </TabsTrigger>
                <TabsTrigger value="past">
                  Past ({getCategoryCount("past")})
                </TabsTrigger>
                <TabsTrigger value="cancelled">
                  Cancelled ({getCategoryCount("cancelled")})
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Bookings List */}
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : filteredBookings.length > 0 ? (
              <>
                {/* Active Bookings Section */}
                {activeCategory !== "cancelled" && (
                  <>
                    <h3 className="text-lg font-semibold mb-4">Active Bookings</h3>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
                      {filteredBookings.filter(booking => booking.status !== "cancelled").map((booking) => (
                        <Card
                          key={`${booking.userId}-${booking.id}`}
                          className="hover:shadow-md transition-shadow"
                        >
                          <CardHeader className="pb-2">
                            <div className="flex justify-between">
                              <CardTitle className="text-lg">{booking.userName}</CardTitle>
                              <Badge variant="outline">
                                #{booking.bookingId?.slice(-6) || "000000"}
                              </Badge>
                            </div>
                            <CardDescription>
                              Booked on: {formatDate(booking.createdAt)}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              <div>
                                <p className="text-sm text-gray-500">Check-in</p>
                                <p className="text-base font-medium">{formatDate(booking.checkIn, booking.checkInTiming)}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Check-out</p>
                                <p className="text-base font-medium">{formatDate(booking.checkOut, booking.checkOutTiming)}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Guests</p>
                                <p className="text-base font-medium">{booking.guests}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Amount</p>
                                <p className="text-base font-semibold">₹{booking.amount}</p>
                              </div>
                            </div>
                            
                            <Separator className="my-3" />
                            <div className="flex justify-between items-center mt-3">
                              <div>
                                <p className="text-sm text-gray-500">Guest Contact</p>
                                <p className="text-sm">{booking.userEmail}</p>
                                <p className="text-sm">{booking.userPhone}</p>
                              </div>
                              
                              <Button
                                onClick={() => handleCancelBooking(booking)}
                                variant="destructive"
                                size="sm"
                              >
                                Cancel Booking
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                )}

                {/* Cancelled Bookings Section */}
                {(activeCategory !== "cancelled" && filteredBookings.some(booking => booking.status === "cancelled")) && (
                  <>
                    <h3 className="text-lg font-semibold mb-4">Cancelled Bookings</h3>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {filteredBookings.filter(booking => booking.status === "cancelled").map((booking) => (
                        <Card
                          key={`${booking.userId}-${booking.id}`}
                          className="opacity-80"
                        >
                          <div className="bg-red-500 text-white text-center py-1 font-medium rounded-t-lg">
                            CANCELLED
                          </div>
                          <CardHeader className="pb-2">
                            <div className="flex justify-between">
                              <CardTitle className="text-lg">{booking.userName}</CardTitle>
                              <Badge variant="destructive">
                                #{booking.bookingId?.slice(-6) || "000000"}
                              </Badge>
                            </div>
                            <CardDescription>
                              Booked on: {formatDate(booking.createdAt)}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              <div>
                                <p className="text-sm text-gray-500">Check-in</p>
                                <p className="text-base font-medium">{formatDate(booking.checkIn, booking.checkInTiming)}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Check-out</p>
                                <p className="text-base font-medium">{formatDate(booking.checkOut, booking.checkOutTiming)}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Guests</p>
                                <p className="text-base font-medium">{booking.guests}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Amount</p>
                                <p className="text-base font-semibold">₹{booking.amount}</p>
                              </div>
                            </div>
                            
                            <Separator className="my-3" />
                            
                            <div className="flex justify-between items-center mt-3">
                              <div>
                                <p className="text-sm text-gray-500">Guest Contact</p>
                                <p className="text-sm">{booking.userEmail}</p>
                                <p className="text-sm">{booking.userPhone}</p>
                              </div>
                              
                              <p className="text-sm text-gray-500">
                                Cancelled on: {formatDate(booking.cancelledAt)}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                )}

                {/* For Cancelled Category */}
                {activeCategory === "cancelled" && (
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {filteredBookings.map((booking) => (
                      <Card
                        key={`${booking.userId}-${booking.id}`}
                        className="opacity-80"
                      >
                        <div className="bg-red-500 text-white text-center py-1 font-medium rounded-t-lg">
                          CANCELLED
                        </div>
                        <CardHeader className="pb-2">
                          <div className="flex justify-between">
                            <CardTitle className="text-lg">{booking.userName}</CardTitle>
                            <Badge variant="destructive">
                              #{booking.bookingId?.slice(-6) || "000000"}
                            </Badge>
                          </div>
                          <CardDescription>
                            Booked on: {formatDate(booking.createdAt)}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <div>
                              <p className="text-sm text-gray-500">Check-in</p>
                              <p className="text-base font-medium">{formatDate(booking.checkIn, booking.checkInTiming)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Check-out</p>
                              <p className="text-base font-medium">{formatDate(booking.checkOut, booking.checkOutTiming)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Guests</p>
                              <p className="text-base font-medium">{booking.guests}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Amount</p>
                              <p className="text-base font-semibold">₹{booking.amount}</p>
                            </div>
                          </div>
                          
                          <Separator className="my-3" />
                          
                          <div className="flex justify-between items-center mt-3">
                            <div>
                              <p className="text-sm text-gray-500">Guest Contact</p>
                              <p className="text-sm">{booking.userEmail}</p>
                              <p className="text-sm">{booking.userPhone}</p>
                            </div>
                            
                            <p className="text-sm text-gray-500">
                              Cancelled on: {formatDate(booking.cancelledAt)}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  {bookingSearchQuery
                    ? "No bookings found matching your search."
                    : "No bookings found in this category."}
                </p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Cancel Booking Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Cancel Booking
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {bookingToCancel && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="font-medium">{bookingToCancel.villaData?.name}</p>
              <p className="text-sm text-gray-600">Guest: {bookingToCancel.userName || "Guest"}</p>
              <p className="text-sm text-gray-600">
                Check-in: {formatDate(bookingToCancel.checkIn, bookingToCancel.checkInTiming)}
              </p>
              <p className="text-sm text-gray-600">
                Check-out: {formatDate(bookingToCancel.checkOut, bookingToCancel.checkOutTiming)}
              </p>
              <p className="text-sm text-gray-600">Booking ID: {bookingToCancel.bookingId}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep Booking
            </Button>
            <Button type="button" variant="destructive" onClick={confirmCancelBooking}>
              Cancel Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calendar Dialog */}
      <Dialog open={showCalendarDialog} onOpenChange={setShowCalendarDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Availability Calendar: {selectedVilla?.name}</DialogTitle>
          </DialogHeader>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="flex items-center mr-4">
                  <div className="w-4 h-4 bg-red-100 rounded-full mr-2"></div>
                  <span className="text-sm">Booked</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-100 rounded-full mr-2"></div>
                  <span className="text-sm">Available</span>
                </div>
              </div>
            </div>
            <Calendar
              mode="multiple"
              selected={bookedDates}
              className="rounded-md border"
              components={{
                Day: renderDay
              }}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setShowCalendarDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}